import {
  Checkbox,
  CircularProgress,
  Dialog,
  FormHelperText,
  IconButton,
  TextField,
  Switch,
} from "@mui/material";
import { useFormik } from "formik";
import { useState } from "react";
import * as yup from "yup";
import { Eye, ScrollText, X } from "lucide-react";
import {
  createTermsAndConditionsAction,
  updateTermsAndConditionsAction,
} from "../../Actions/termsAndConditionsAction";
import TinyMceEditorComponent from "../../components/Common/TinyMceEditorComponent";
import PreviewTemplateModel from "./TermsAndConditionPreviewModal";
import { FRONTEND_MESSAGES } from "../../utils/messages";
import { containsBadWords } from "../../utils/badWord";
import "../../components/Common/FormModal.css";
import "./AddEditTermsConditionModal.css";

const { VALIDATION } = FRONTEND_MESSAGES;

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#22419e" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#22419e",
  },
};

const AddEditTermsConditionModal = ({
  open,
  handleClose,
  templateDataById,
  setSnackbar,
  getTemplateData,
}) => {
  const [loading, setLoading] = useState(false);
  const [openPreviewModel, setOpenPreviewModel] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);

  const isEdit = Boolean(templateDataById);

  const handleCloseModal = () => {
    setOpenPreviewModel(false);
  };

  const handlePreviewModel = () => {
    setOpenPreviewModel(true);
  };

  const validationSchema = yup
    .object({
      templateTitle: yup
        .string()
        .trim()
        .min(3, VALIDATION.MIN_LENGTH("Title", 3))
        .max(100, VALIDATION.MAX_LENGTH("Title", 100))
        .test(
          "bad-word",
          "Title contains inappropriate language.",
          (value) => !containsBadWords(value || "")
        )
        .matches(/^[a-zA-Z0-9\s_\-]+$/, VALIDATION.INVALID_FORMAT("Title"))
        .required(VALIDATION.REQUIRED_FIELD("Title")),
      templateContent: yup
        .string()
        .trim()
        .required(VALIDATION.REQUIRED_FIELD("Template content"))
        .test(
          "bad-word",
          "Template Content contains inappropriate language.",
          (value) => !containsBadWords(value || "")
        ),
      isMaster: yup.boolean(),
      isTenant: yup.boolean(),
      isActive: yup.boolean(),
    })
    .test(
      "at-least-one-type",
      VALIDATION.AT_LEAST_ONE_REQUIRED(["Master", "Tenant"]),
      function (values) {
        const { isMaster, isTenant } = values;
        if (!isMaster && !isTenant) {
          return this.createError({
            path: "isMaster",
            message: VALIDATION.AT_LEAST_ONE_REQUIRED(["Master", "Tenant"]),
          });
        }
        return true;
      }
    );

  const formik = useFormik({
    initialValues: {
      templateTitle: templateDataById?.title || "",
      templateContent: templateDataById?.content || "",
      isMaster: templateDataById?.isMaster ?? false,
      isTenant: templateDataById?.isTenant ?? false,
      isRequired: templateDataById?.isRequired ?? false,
      isActive: templateDataById?.isActive ?? true,
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setButtonDisable(true);
      setLoading(true);

      try {
        const payload = {
          templateTitle: values.templateTitle,
          templateContent: values.templateContent,
          isMaster: values.isMaster,
          isTenant: values.isTenant,
          isActive: values.isActive,
          isRequired: values.isRequired,
        };
        if (templateDataById) {
          const res = await updateTermsAndConditionsAction(
            payload,
            templateDataById.id
          );
          setSnackbar({
            open: true,
            message: res.message || "Terms & Conditions updated successfully",
            severity: "success",
          });
        } else {
          const res = await createTermsAndConditionsAction(payload);
          setSnackbar({
            open: true,
            message: res.message || "Terms & Conditions created successfully",
            severity: "success",
          });
        }

        formik.resetForm();
        handleClose();
        getTemplateData();
      } catch (error) {
        setSnackbar({
          open: true,
          message: error?.response?.data?.message || "Something went wrong!",
          severity: "error",
        });
      } finally {
        setLoading(false);
        setButtonDisable(false);
      }
    },
  });

  const handleCheckboxChange = (e) => {
    formik.handleChange(e);
    formik.setFieldTouched(e.target.name, true, false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ className: "fm-dialog tcm-dialog" }}
    >
      <div className="fm-header">
        <span className="fm-header-icon" aria-hidden>
          <ScrollText size={20} strokeWidth={2} />
        </span>
        <div className="fm-header-copy">
          <h2>
            {isEdit ? "Update terms & conditions" : "Add terms & conditions"}
          </h2>
          <p>
            {isEdit
              ? "Change content, audience, and publication status."
              : "Create terms content and choose where it applies."}
          </p>
        </div>
        <IconButton
          className="fm-close"
          onClick={handleClose}
          aria-label="Close"
          size="small"
        >
          <X size={18} strokeWidth={2} />
        </IconButton>
      </div>

      <form onSubmit={formik.handleSubmit} className="fm-form">
        <div className="fm-body">
          <section className="fm-section">
            <div className="fm-section-head">
              <h3>Details</h3>
              <p>Title shown to users when they review these terms.</p>
            </div>
            <div className="fm-card">
              <div className="fm-grid is-stack">
                <div className="fm-field">
                  <label htmlFor="tcm-title">
                    Title<span className="fm-req">*</span>
                  </label>
                  <TextField
                    id="tcm-title"
                    fullWidth
                    size="small"
                    name="templateTitle"
                    placeholder="Enter template title"
                    value={formik.values.templateTitle}
                    onChange={(e) => {
                      formik.setFieldValue(
                        "templateTitle",
                        e.target.value.replace(/^\s+/, "")
                      );
                      formik.setFieldTouched("templateTitle", true, false);
                    }}
                    onBlur={(e) => {
                      formik.setFieldValue(
                        "templateTitle",
                        e.target.value.trim()
                      );
                      formik.handleBlur(e);
                    }}
                    error={
                      formik.touched.templateTitle &&
                      Boolean(formik.errors.templateTitle)
                    }
                    helperText={
                      formik.touched.templateTitle &&
                      formik.errors.templateTitle
                    }
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="fm-section">
            <div className="fm-section-head">
              <h3>Content</h3>
              <p>Full terms text presented in the acceptance flow.</p>
            </div>
            <div className="fm-card tcm-editor-card">
              <div className="fm-field is-full">
                <label>
                  Template content<span className="fm-req">*</span>
                </label>
                <div
                  className={`tcm-editor${
                    formik.touched.templateContent &&
                    formik.errors.templateContent
                      ? " is-error"
                      : ""
                  }`}
                >
                  <TinyMceEditorComponent
                    key={templateDataById?.id || "new"}
                    initialValue={formik.values.templateContent}
                    disablePlugins={["media", "image"]}
                    handleChange={(content) => {
                      formik.setFieldValue("templateContent", content);
                      formik.setFieldTouched("templateContent", true, false);
                    }}
                    handleBlur={(content) => {
                      const trimmedContent = content?.trim() || "";
                      formik.setFieldValue("templateContent", trimmedContent);
                      formik.setFieldTouched("templateContent", true);
                    }}
                    isDisabled={loading}
                  />
                </div>
                {formik.touched.templateContent &&
                  formik.errors.templateContent && (
                    <FormHelperText error>
                      {formik.errors.templateContent}
                    </FormHelperText>
                  )}
              </div>
            </div>
          </section>

          <section className="fm-section">
            <div className="fm-section-head">
              <h3>Audience & options</h3>
              <p>Where these terms apply and how they behave.</p>
            </div>
            <div className="fm-card">
              <div className="fm-field is-full" style={{ marginBottom: 14 }}>
                <label>
                  Template type<span className="fm-req">*</span>
                </label>
                <div className="tcm-type-row">
                  <label
                    className={`tcm-check${
                      formik.values.isMaster ? " is-on" : ""
                    }`}
                  >
                    <Checkbox
                      name="isMaster"
                      checked={formik.values.isMaster}
                      onChange={handleCheckboxChange}
                      onBlur={formik.handleBlur}
                      size="small"
                      sx={{
                        color: "#94a3b8",
                        "&.Mui-checked": { color: "#22419e" },
                      }}
                    />
                    <span>
                      <strong>Master</strong>
                      <small>Applies on the master portal</small>
                    </span>
                  </label>
                  <label
                    className={`tcm-check${
                      formik.values.isTenant ? " is-on" : ""
                    }`}
                  >
                    <Checkbox
                      name="isTenant"
                      checked={formik.values.isTenant}
                      onChange={handleCheckboxChange}
                      onBlur={formik.handleBlur}
                      size="small"
                      sx={{
                        color: "#94a3b8",
                        "&.Mui-checked": { color: "#22419e" },
                      }}
                    />
                    <span>
                      <strong>Tenant</strong>
                      <small>Applies for tenant portals</small>
                    </span>
                  </label>
                </div>
                {formik.errors.isMaster && (
                  <FormHelperText error>{formik.errors.isMaster}</FormHelperText>
                )}
              </div>

              <div className="fm-toggle-list">
                <label
                  className={`fm-toggle${
                    formik.values.isRequired ? " is-on" : ""
                  }`}
                >
                  <span>
                    <p className="fm-toggle-title">Required</p>
                    <p className="fm-toggle-sub">
                      Users must accept these terms before continuing.
                    </p>
                  </span>
                  <Switch
                    name="isRequired"
                    checked={formik.values.isRequired}
                    onChange={formik.handleChange}
                    inputProps={{ "aria-label": "Required" }}
                    sx={switchSx}
                  />
                </label>

                <label
                  className={`fm-toggle${
                    formik.values.isActive ? " is-on" : ""
                  }`}
                >
                  <span>
                    <p className="fm-toggle-title">Active</p>
                    <p className="fm-toggle-sub">
                      Inactive terms are hidden from acceptance flows.
                    </p>
                  </span>
                  <Switch
                    name="isActive"
                    checked={formik.values.isActive}
                    onChange={formik.handleChange}
                    inputProps={{ "aria-label": "Active" }}
                    sx={switchSx}
                  />
                </label>
              </div>
            </div>
          </section>
        </div>

        <div className="fm-footer tcm-footer">
          <button
            type="button"
            className="fm-btn"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </button>
          <div className="tcm-footer-actions">
            {isEdit && (
              <button
                type="button"
                className="fm-btn"
                onClick={handlePreviewModel}
                disabled={loading}
              >
                <Eye size={15} strokeWidth={2} aria-hidden />
                Preview
              </button>
            )}
            <button
              type="submit"
              className="fm-btn is-primary"
              disabled={!formik.isValid || buttonDisable || loading}
            >
              {loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : isEdit ? (
                "Update"
              ) : (
                "Create"
              )}
            </button>
          </div>
        </div>
      </form>

      {openPreviewModel && (
        <PreviewTemplateModel
          open={openPreviewModel}
          handleClose={handleCloseModal}
          contentData={{
            ...templateDataById,
            title: formik.values.templateTitle,
            content: formik.values.templateContent,
          }}
        />
      )}
    </Dialog>
  );
};

export default AddEditTermsConditionModal;
